import { BlockType } from '../../../models/enums';
import { events } from '../../../common/constant';
import * as constants from '../../../common/constant';
import { getNormalizedKey } from '../../../common/utils/common';
import { getAdjacentBlock, getBlockModelById } from '../../../common/utils/block';
import { getSelectedRange } from '../../../common/utils/selection';
/**
 * `ContextMenuModule` is used to handle the context menu actions in the BlockEditor.
 *
 * @hidden
 */
export class ContextMenuModule {
    constructor(manager) {
        this.isPopupOpened = false;
        this.isClipboardEmptyCache = true;
        this.shortcutMap = new Map();
        this.parent = manager;
        this.addEventListeners();
    }
    addEventListeners() {
        this.parent.observer.on(events.keydown, this.onKeyDown, this);
        this.parent.observer.on('contextMenuCreated', this.handleContextMenuCreated, this);
        this.parent.observer.on('contextMenuBeforeOpen', this.handleContextMenuBeforeOpen, this);
        this.parent.observer.on('updateContextMenuState', this.updateContextMenuPopupState, this);
        this.parent.observer.on('contextMenuSelection', this.handleContextMenuSelection, this);
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    removeEventListeners() {
        this.parent.observer.off(events.keydown, this.onKeyDown);
        this.parent.observer.off('contextMenuCreated', this.handleContextMenuCreated);
        this.parent.observer.off('contextMenuBeforeOpen', this.handleContextMenuBeforeOpen);
        this.parent.observer.off('updateContextMenuState', this.updateContextMenuPopupState);
        this.parent.observer.off('contextMenuSelection', this.handleContextMenuSelection);
        this.parent.observer.off(events.destroy, this.destroy);
    }
    handleContextMenuCreated() {
        this.buildShortcutMap();
    }
    buildShortcutMap() {
        this.shortcutMap.clear();
        this.parent.contextMenuSettings.items.forEach((item) => {
            this.shortcutMap.set(item.shortcut.toLowerCase(), item);
        });
    }
    onKeyDown(e) {
        const normalizedKey = getNormalizedKey(e);
        if (!normalizedKey) {
            return;
        }
        const menuItem = this.shortcutMap.get(normalizedKey);
        if (menuItem && menuItem.id !== 'cut' && menuItem.id !== 'copy' && menuItem.id !== 'paste') {
            e.preventDefault();
            this.handleContextMenuActions(menuItem, e);
        }
    }
    handleContextMenuBeforeOpen(args) {
        if (!this.parent.currentFocusedBlock) {
            this.parent.setFocusAndUIForNewBlock(this.parent.currentHoveredBlock);
        }
        this.toggleDisabledItems();
        this.parent.blockActionMenuModule.toggleBlockActionPopup(true);
        this.parent.linkModule.hideLinkPopup();
        setTimeout(() => {
            if (this.parent.inlineToolbarModule) {
                this.parent.inlineToolbarModule.hideInlineToolbar(args.event);
            }
        }, 50);
    }
    updateContextMenuPopupState(value) {
        this.isPopupOpened = value.isOpen;
    }
    handleContextMenuSelection(args) {
        this.handleContextMenuActions(args.item, args.event);
    }
    handleIndentationAction(shouldDecrease) {
        this.parent.execCommand({ command: 'IndentBlock', state: {
                blockIDs: this.parent.editorMethods.getSelectedBlocks().map((block) => block.id),
                shouldDecrease
            } });
    }
    handleContextMenuActions(menuItem, e) {
        const prop = menuItem.id.toLowerCase();
        switch (prop) {
            case 'undo':
                this.parent.undoRedoAction.undo();
                break;
            case 'redo':
                this.parent.undoRedoAction.redo();
                break;
            case 'cut':
                this.parent.clipboardAction.handleContextCut();
                break;
            case 'copy':
                this.parent.clipboardAction.handleContextCopy();
                break;
            case 'paste':
                this.parent.clipboardAction.handleContextPaste();
                break;
            case 'link':
                this.parent.linkModule.showLinkPopup(e);
                break;
            case 'increaseindent':
            case 'decreaseindent':
                this.handleIndentationAction(prop === 'decreaseindent');
                break;
        }
    }
    toggleDisabledItems() {
        if (!getSelectedRange() || !this.parent.currentFocusedBlock) {
            return;
        }
        const blockModel = getBlockModelById(this.parent.currentFocusedBlock.id, this.parent.getEditorBlocks());
        const tableBlk = this.parent.currentFocusedBlock.closest(`.${constants.TABLE_BLOCK_CLS}`);
        const notAllowedTypes = [BlockType.Image, BlockType.Code];
        const isNotAllowedType = notAllowedTypes.indexOf(blockModel.blockType) !== -1;
        const previousBlockElement = getAdjacentBlock(this.parent.currentFocusedBlock, 'previous');
        const previousBlockModel = previousBlockElement
            ? getBlockModelById(previousBlockElement.id, this.parent.getEditorBlocks())
            : null;
        const canIndent = (!tableBlk && (!previousBlockModel ||
            (previousBlockModel && blockModel.indent <= previousBlockModel.indent) && !isNotAllowedType));
        const canOutdent = !tableBlk && (blockModel.indent > 0 && !isNotAllowedType);
        const isSelection = getSelectedRange().toString().trim().length > 0;
        const selectedBlocks = this.parent.editorMethods.getSelectedBlocks();
        const canAllowLink = isSelection && !isNotAllowedType && (selectedBlocks && selectedBlocks.length === 1);
        const menuState = {
            'increaseindent': canIndent,
            'decreaseindent': canOutdent,
            'undo': this.parent.undoRedoAction.canUndo(),
            'redo': this.parent.undoRedoAction.canRedo(),
            'link': canAllowLink,
            'cut': isSelection,
            'copy': isSelection,
            'paste': true
        };
        this.parent.observer.notify('enableDisableContextMenuItems', menuState);
    }
    /**
     * Checks whether the context menu is opened or not.
     *
     * @returns {boolean} - Returns true if the context menu is opened, otherwise false.
     * @hidden
     */
    isPopupOpen() {
        return this.isPopupOpened;
    }
    /**
     * Destroys the ContextMenu module.
     *
     * @returns {void}
     */
    destroy() {
        this.removeEventListeners();
        this.shortcutMap = null;
    }
}
